import LineLabel from "./LineLabel";
import StyledTreeItem from "../general/tree/StyledTreeItem";
import React from "react";
import {Divider, Menu, MenuItem} from "@mui/material";
import {useSnackbar} from "notistack";
import {useTranslation} from "react-i18next";
import PropTypes from "prop-types";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import ContextMenuItemLabel from "../general/menuItem/ContextMenuItemLabel";

export default function Line(props) {
    const {
        node,
        addLine,
        deleteLine,
        onValueChange,
        onInputFocus,
        children,
        ...other
    } = props;
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation("common");
    // Context menu state
    const initialState = {
        mouseX: null,
        mouseY: null,
    };
    const [state, setState] = React.useState(initialState);

    const copy = (value) => {
        navigator.clipboard.writeText(value).then(function () {
            /* clipboard successfully set */
            enqueueSnackbar(t("main.components.dataViewer.notification.copy"), {variant: "success"});
        }, function () {
            /* clipboard write failed */
            enqueueSnackbar(t("main.components.dataViewer.notification.copyfailure"), {variant: "error"});
        });
    }

    const selectInput = () => {
        let root = document.getElementById(node.id);
        if (root)
            return root.querySelector("textarea");
        return null;
    }

    const focusInput = () => {
        if (node.parent.editable) {
            let input = selectInput();
            if (input)
                input.focus();
        }
    }

    // Context menu event

    const handleContextMenu = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setState({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
        });
    };

    const handleCloseContextMenu = () => {
        setState(initialState);
    };

    const handleCopy = () => {
        copy(node.label);
        handleCloseContextMenu();
    }

    const handlePaste = () => {
        let input = selectInput();
        if (input)
            navigator.clipboard.readText().then(clipText => {
                if (input.selectionStart || input.selectionStart === 0) {
                    let startPos = input.selectionStart;
                    let endPos = input.selectionEnd;
                    let value = input.value.substring(0, startPos)
                        + clipText
                        + input.value.substring(endPos, input.value.length);
                    onValueChange(value);
                } else {
                    onValueChange(input.value + clipText);
                }
            });
        handleCloseContextMenu();
    }

    const handleAddLine = (isAbove) => {
        addLine(isAbove);
        handleCloseContextMenu();
    }

    const handleDialogDelete = () => {
        handleCloseContextMenu();
        deleteLine();
    }

    // END MENU

    return (
        <StyledTreeItem
            {...other}
            onDoubleClick={focusInput}
            onContextMenu={handleContextMenu}
            label={
                <>
                    <LineLabel
                        value={node.label}
                        onValueChange={onValueChange}
                        onInputFocus={onInputFocus}
                        editable={node.parent.editable}>
                        {children}
                    </LineLabel>
                    <Menu
                        open={state.mouseY !== null}
                        onClose={handleCloseContextMenu}
                        anchorReference="anchorPosition"
                        anchorPosition={
                            state.mouseY !== null && state.mouseX !== null
                                ? {top: state.mouseY, left: state.mouseX}
                                : undefined
                        }
                    >
                        <MenuItem onClick={handleCopy}>
                            <ContextMenuItemLabel
                                icon={<ContentCopyIcon fontSize="small"/>}
                                shortcut="Ctrl+c"
                            >
                                {t("main.components.dataViewer.contextMenu.copy")}
                            </ContextMenuItemLabel>
                        </MenuItem>
                        {node.parent.editable && [
                            <MenuItem key="paste" onClick={handlePaste}>
                                <ContextMenuItemLabel
                                    icon={<ContentPasteIcon fontSize="small"/>}
                                    shortcut="Ctrl+v"
                                >
                                    {t("main.components.dataViewer.contextMenu.paste")}
                                </ContextMenuItemLabel>
                            </MenuItem>,
                            <MenuItem key="delete" onClick={handleDialogDelete}>
                                <ContextMenuItemLabel
                                    icon={<DeleteForeverIcon fontSize="small"/>}
                                    shortcut="Ctrl+d"
                                >
                                    {t("main.components.dataViewer.contextMenu.deleteLine")}
                                </ContextMenuItemLabel>
                            </MenuItem>,
                            <Divider key="divider-01" sx={{margin: theme => theme.spacing(0, 1, 0, 1)}}/>,
                            <MenuItem key="add-below" onClick={() => {
                                handleAddLine(false);
                            }}>
                                <ContextMenuItemLabel
                                    icon={<AddIcon fontSize="small"/>}
                                    shortcut="Ctrl+l"
                                >
                                    {t("main.components.dataViewer.contextMenu.addBelow")}
                                </ContextMenuItemLabel>
                            </MenuItem>,
                            <MenuItem key="add-above" onClick={() => {
                                handleAddLine(true);
                            }}>
                                <ContextMenuItemLabel
                                    icon={<AddIcon fontSize="small"/>}
                                    shortcut="Ctrl+Maj+l"
                                >
                                    {t("main.components.dataViewer.contextMenu.addAbove")}
                                </ContextMenuItemLabel>
                            </MenuItem>
                        ]}
                    </Menu>
                </>
            }
        />
    );
}

Line.propTypes = {
    node: PropTypes.object.isRequired,
    addLine: PropTypes.func.isRequired,
    deleteLine: PropTypes.func.isRequired,
    onValueChange: PropTypes.func.isRequired,
    onInputFocus: PropTypes.func
}

Line.defaultProps = {
    onInputFocus: () => {
    }
}